import { useMemo } from 'react';
import { TierCard } from '../components/TierCard';
import type { TierLadderEntry } from '../../../../services/rewards/RewardsService';

/**
 * A rung of the ladder as the screen renders it.
 *
 * `componentChecks` is populated ONLY for the next tier, because that is the one
 * "see how to get to the next tier" explains — which checks failed and by how
 * much. Higher rungs are pulled live from the tier collection as display copy and
 * deliberately carry no per-machine detail, so nothing about them is stored on
 * the machine.
 */
export interface Tier0LadderRung {
  tierId: string;
  name: string;
  earnRateDescription?: string;
  maxEarningsDescription?: string;
  componentChecks?: unknown[];
  /** True for the immediate next tier — the only one that shows the checks. */
  isNext: boolean;
}

export interface BuildLadderInput {
  /** The machine's assigned tier id, or null when unevaluated. */
  assignedTierId: string | null;
  /** The next tier from the machine's stored detail — the only one with checks. */
  nextTier: {
    tierId: string;
    name: string;
    earnRateDescription?: string;
    maxEarningsDescription?: string;
    componentChecks?: unknown[];
  } | null;
  /** Live, ascending tier collection. */
  ladder: TierLadderEntry[];
}

/**
 * Builds the ladder above the machine's assignment.
 *
 * Rungs STRICTLY above the assignment, ascending. The next tier (from the stored
 * detail) carries its component checks and overrides the live copy for that rung;
 * every other rung is live display data only. With no assignment (unevaluated)
 * every rung is a live preview — there is no "next" to explain yet.
 */
export function buildTier0Ladder({ assignedTierId, nextTier, ladder }: BuildLadderInput): Tier0LadderRung[] {
  const assignedPriority = assignedTierId
    ? ladder.find((t) => t.tierId === assignedTierId)?.priority ?? null
    : null;

  const above = ladder.filter((t) => {
    if (assignedPriority === null) return true;
    return t.priority > assignedPriority && t.tierId !== assignedTierId;
  });

  return above.map((tier) => {
    const isNext = nextTier?.tierId === tier.tierId;
    return {
      tierId: tier.tierId,
      name: tier.name,
      earnRateDescription: isNext ? nextTier!.earnRateDescription ?? tier.earnRateDescription : tier.earnRateDescription,
      maxEarningsDescription: isNext ? nextTier!.maxEarningsDescription ?? tier.maxEarningsDescription : tier.maxEarningsDescription,
      // Only the next rung carries this level of detail; higher rungs must not.
      componentChecks: isNext ? nextTier!.componentChecks ?? [] : undefined,
      isNext,
    };
  });
}

interface Tier0LockedTierPreviewsProps {
  /** The next tier from the machine's stored detail — the only rung with checks. */
  nextTier: BuildLadderInput['nextTier'];
  /** Live tier collection, ascending. */
  ladder?: TierLadderEntry[];
  /** The machine's assigned tier, so only rungs ABOVE it are shown. */
  assignedTierId?: string | null;
}

/** Locked preview cards for every rung above the assignment (welcome + about tiers). */
export const Tier0LockedTierPreviews = ({
  nextTier,
  ladder = [],
  assignedTierId = null,
}: Tier0LockedTierPreviewsProps) => {
  const rungs = useMemo(
    () => buildTier0Ladder({ assignedTierId, nextTier, ladder }),
    [assignedTierId, nextTier, ladder],
  );

  return (
    <>
      {rungs.map((rung) => (
        <TierCard
          key={rung.tierId}
          name={rung.name}
          earnRateDescription={rung.earnRateDescription}
          maxEarningsDescription={rung.maxEarningsDescription}
          variant="next"
        />
      ))}
    </>
  );
};

